import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, Grid, Button, FormHelperText, FormControl, InputLabel, Select, MenuItem, Box } from '@mui/material';
import { 
    useCategoriesPartialUpdateMutation, 
    useSalonsMySalonsRetrieveQuery, 
    useSalonsCategoriesListQuery,
    useCategoriesDefaultRetrieveQuery 
} from '../../../../services/GLOWClient';
import MuiDialog from '../../../../SystemDesign/MuiDialog';
import theme from '../../../../theme';
import EditIcon from '@mui/icons-material/Edit';
import TranslateIcon from '@mui/icons-material/Translate';
import TranslationValidationModal from '../TranslationValidationModal';

const UpdateCategory = ({ open, onClose, onSuccess, categoryData, salonId }) => {
    const { t, i18n } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        description: ''
    });
    const [errors, setErrors] = useState({});
    const [dialogClosed, setDialogClosed] = useState(false);
    const [translationModalOpen, setTranslationModalOpen] = useState(false);
    const [translationModalLoading, setTranslationModalLoading] = useState(false);
    const [pendingTranslations, setPendingTranslations] = useState(null);
    const [pendingUpdatedCategory, setPendingUpdatedCategory] = useState(null);
    const [updatedCategoryDraft, setUpdatedCategoryDraft] = useState({ name: '', description: '' });
    const pollIntervalRef = useRef(null);
    const currentLanguage = (i18n.language || 'fr').split('-')[0].toLowerCase();

    const getTranslatedField = React.useCallback((item, field) => {
        const translationsKey = `${field}_translations`;
        const localizedKey = `localized_${field}`;
        const translations = item?.[translationsKey] || {};

        if (translations[currentLanguage]) {
            return translations[currentLanguage];
        }

        if (item?.[localizedKey]) {
            return item[localizedKey];
        }

        for (const fallbackLanguage of ['fr', 'en', 'ar']) {
            if (translations[fallbackLanguage]) {
                return translations[fallbackLanguage];
            }
        }

        return item?.[field] || '';
    }, [currentLanguage]);

    const shouldUpdateRawField = React.useCallback((item, field) => {
        const rawValue = item?.[field] || '';
        const currentTranslatedValue = item?.[`${field}_translations`]?.[currentLanguage]
            || item?.[`localized_${field}`]
            || '';

        return !currentTranslatedValue || currentTranslatedValue === rawValue;
    }, [currentLanguage]);

    useEffect(() => () => {
        clearInterval(pollIntervalRef.current);
    }, []);

    // RTK Query hooks
    const [updateCategory, { isLoading: isUpdating }] = useCategoriesPartialUpdateMutation();
    const { data: mySalonsData, isLoading: salonsLoading } = useSalonsMySalonsRetrieveQuery();
    const { data: existingCategories = [] } = useSalonsCategoriesListQuery(
        salonId ? { salonId: Number(salonId), language: i18n.language } : undefined,
        { skip: !salonId }
    );
    const { data: defaultCategories = [] } = useCategoriesDefaultRetrieveQuery();

    // Check if the current category is a default category
    const isDefaultCategory = useMemo(() => {
        // Use the flag from backend if available
        if (categoryData?.is_default !== undefined) {
            return categoryData.is_default;
        }

        // Fallback to name check if flag is missing (legacy support)
        if (!categoryData?.name || !defaultCategories?.length) return false;

        const defaultCategoryNames = defaultCategories.map(cat => cat.name.trim().toLowerCase());
        return defaultCategoryNames.includes(categoryData.name.trim().toLowerCase());
    }, [categoryData, defaultCategories]);

    // Reset dialog state when open/close changes
    useEffect(() => {
        if (open === false) {
            setDialogClosed(true);
        } else {
            setDialogClosed(false);
        }
    }, [open]);

    // Initialize form with category data when it changes
    useEffect(() => {
        if (categoryData && open) {
            setFormData({
                name: getTranslatedField(categoryData, 'name'),
                description: getTranslatedField(categoryData, 'description')
            });

            // Clear any previous errors when opening with new data
            setErrors({});
        }
    }, [categoryData, getTranslatedField, open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when field is edited
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        const name = formData.name?.trim();

        if (!name) {
            newErrors.name = t('pro_category.update_category.name_required_error');
        } else if (!isDefaultCategory) { // Only check for duplicates on non-default categories
            const isDuplicate = existingCategories?.some(cat =>
                getTranslatedField(cat, 'name').toLowerCase() === name.toLowerCase() &&
                cat.salon === salonId &&
                (!categoryData || cat.category_id !== categoryData.category_id) // Exclude current category from check
            );

            if (isDuplicate) {
                newErrors.name = t('pro_category.update_category.duplicate_name_error');
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const hasGeneratedTranslations = (translations = {}) => {
        return ['fr', 'en', 'ar'].some(
            (lang) => lang !== currentLanguage && Boolean(translations?.[lang])
        );
    };

    const handleSaveTranslations = async (editedTranslations) => {
        setTranslationModalOpen(false);
        clearInterval(pollIntervalRef.current);

        if (!categoryData?.category_id) return;

        try {
            const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
            await fetch(`/api/categories/${categoryData.category_id}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    name_translations: editedTranslations.name,
                    description_translations: editedTranslations.description,
                }),
            });
        } catch (e) {
            console.warn('[category translations] patch error', e);
        }

        if (onSuccess) onSuccess(pendingUpdatedCategory);
    };

    const handleTranslationSkip = () => {
        setTranslationModalOpen(false);
        clearInterval(pollIntervalRef.current);
        if (onSuccess) onSuccess(pendingUpdatedCategory);
    };

    const handleSubmit = async (withTranslationCheck = false) => {
        if (!validateForm()) return;

        try {
            if (!categoryData || !categoryData.category_id) {
                console.error('Missing category ID for update');
                setErrors({ general: t('pro_category.update_category.update_error_id_missing') });
                return;
            }

            // Prepare the update data
            const trimmedName = formData.name.trim();
            const trimmedDescription = formData.description?.trim() || '';
            const shouldForceRawUpdate = withTranslationCheck && !isDefaultCategory;
            const shouldUpdateRawName = shouldForceRawUpdate || shouldUpdateRawField(categoryData, 'name');
            const shouldUpdateRawDescription = shouldForceRawUpdate || shouldUpdateRawField(categoryData, 'description');
            const updateData = {
                categoryId: categoryData.category_id,
                salon: salonId
            };

            if (shouldUpdateRawName) {
                updateData.name = trimmedName;
            }

            if (!shouldUpdateRawName || categoryData?.name_translations?.[currentLanguage]) {
                updateData.name_translations = {
                    ...(categoryData?.name_translations || {}),
                    [currentLanguage]: trimmedName
                };
            }

            if (formData.description !== undefined) {
                if (shouldUpdateRawDescription) {
                    updateData.description = trimmedDescription;
                }

                if (!shouldUpdateRawDescription || categoryData?.description_translations?.[currentLanguage]) {
                    updateData.description_translations = {
                        ...(categoryData?.description_translations || {}),
                        [currentLanguage]: trimmedDescription
                    };
                }
            }

            const result = await updateCategory(updateData).unwrap();
            setPendingUpdatedCategory(result);
            setUpdatedCategoryDraft({ name: trimmedName, description: trimmedDescription });

            if (withTranslationCheck && !isDefaultCategory) {
                setTranslationModalOpen(true);
                setTranslationModalLoading(true);
                setPendingTranslations(null);

                let attempts = 0;
                const maxAttempts = 20;
                pollIntervalRef.current = setInterval(async () => {
                    attempts++;

                    try {
                        const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
                        const response = await fetch(`/api/categories/${categoryData.category_id}/`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });

                        if (response.ok) {
                            const data = await response.json();
                            const categoryTranslations = {
                                name: data.name_translations || {},
                                description: data.description_translations || {},
                            };

                            if (hasGeneratedTranslations(categoryTranslations.name) || attempts >= maxAttempts) {
                                clearInterval(pollIntervalRef.current);
                                setPendingTranslations(categoryTranslations);
                                setTranslationModalLoading(false);
                            }
                        }
                    } catch (e) {
                        console.warn('[category translations] poll error', e);
                        if (attempts >= maxAttempts) {
                            clearInterval(pollIntervalRef.current);
                            setPendingTranslations({
                                name: { [currentLanguage]: trimmedName },
                                description: { [currentLanguage]: trimmedDescription },
                            });
                            setTranslationModalLoading(false);
                        }
                    }
                }, 1500);
            } else {
                if (onSuccess) onSuccess(result);
            }
        } catch (error) {
            console.error('Failed to update category:', error);

            // Handle API validation errors
            if (error.data) {
                const apiErrors = {};
                Object.entries(error.data).forEach(([key, value]) => {
                    apiErrors[key] = Array.isArray(value) ? value.join(' ') : value;
                });
                setErrors(apiErrors);
            }
        }
    };

    const handleDialogClose = () => {
        // Reset errors when dialog is closed
        clearInterval(pollIntervalRef.current);
        setErrors({});
        setTranslationModalOpen(false);
        setTranslationModalLoading(false);
        setPendingTranslations(null);
        setPendingUpdatedCategory(null);
        setUpdatedCategoryDraft({ name: '', description: '' });
        onClose();
    };

    // Content to be displayed in the dialog
    const dialogContent = (
        <Box sx={{ width: '100%' }}>
            <Grid container spacing={2} direction="column">

                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label={t('pro_category.update_category.category_name')}
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        disabled={isDefaultCategory}
                        error={!!errors.name}
                        helperText={errors.name || (isDefaultCategory ? t('pro_category.update_category.default_category_name_locked') : "")}
                        margin="normal"
                        variant="outlined"
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label={t('pro_category.update_category.description')}
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        multiline
                        rows={4}
                        error={!!errors.description}
                        helperText={errors.description}
                        margin="normal"
                        variant="outlined"
                        InputProps={{
                            style: { overflow: 'auto' }
                        }}
                    />
                </Grid>

                {!isDefaultCategory && (
                    <Grid item xs={12}>
                        <Button
                            variant="outlined"
                            onClick={() => handleSubmit(true)}
                            disabled={isUpdating}
                            startIcon={<TranslateIcon fontSize="small" />}
                            sx={{
                                width: '100%',
                                borderColor: theme.colors.primary[400],
                                color: theme.colors.primary[700],
                                fontSize: '0.75rem',
                                padding: '8px 12px',
                                textTransform: 'none',
                                '&:hover': {
                                    borderColor: theme.colors.primary[600],
                                    backgroundColor: theme.colors.primary[50]
                                }
                            }}
                        >
                            {t('pro_salon.translations.verify_translations', 'Vérifier traduction')}
                        </Button>
                    </Grid>
                )}
            </Grid>
        </Box>
    );

    // Trigger button for the dialog - this is hidden as we're controlling the dialog externally
    const triggerButton = <Button style={{ display: 'none' }}>Open</Button>;

    return (
        <>
            <MuiDialog
                open={open}
                icon={<EditIcon style={{ color: theme.colors.primary[500] }} />}
                triggerButton={triggerButton}
                title={t('pro_category.update_category.edit_category_title')}
                subtitle={t('pro_category.update_category.edit_category_subtitle', { name: getTranslatedField(categoryData, 'name') || '' })}
                content={dialogContent}
                existAction={true}
                actionTitle={t('pro_category.update_category.save')}
                actionFunction={() => handleSubmit(false)}
                onClose={dialogClosed}
                onExternalClose={handleDialogClose}
                isLoading={isUpdating}
                actionType="button"
                maxWidth="sm"
                disableAction={isUpdating}
            />

            <TranslationValidationModal
                open={translationModalOpen}
                onClose={handleTranslationSkip}
                onValidate={handleSaveTranslations}
                salonName={updatedCategoryDraft.name}
                salonDescription={updatedCategoryDraft.description}
                isLoading={translationModalLoading}
                translations={pendingTranslations}
            />
        </>
    );
};

export default UpdateCategory;